import React, { useEffect, useRef, useState } from 'react'
import { ConnectWalletButton } from '../auth/ConnectWalletButton'
import { usePortfolioStream } from '../../ws/usePortfolioStream'
import { useAuth } from '../../auth/AuthContext'
import type { PortfolioSummary } from '../../types/portfolio'

function StreamIndicator() {
  const { portfolioId, token, updateTokens } = useAuth()
  const { status } = usePortfolioStream(portfolioId, token, updateTokens)

  const dot: Record<string, string> = {
    connected:    'bg-accent-green',
    connecting:   'bg-accent-yellow animate-pulse',
    disconnected: 'bg-gray-600',
    error:        'bg-accent-red',
  }
return (
    <span className={`inline-block h-2 w-2 rounded-full ${dot[status] ?? 'bg-gray-600'}`} />
  )
}

function hfColor(hf: number) {
  if (hf >= 2.0)  return 'text-accent-green'
  if (hf >= 1.55) return 'text-amber-400'
  return 'text-accent-red'
}

function HeaderMetrics({ summary }: { summary: PortfolioSummary }) {
  const navNum = parseFloat(summary.navUsd)
  const nav = '$' + navNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const hf = parseFloat(summary.healthFactor)
  const hfDisplay = hf >= 999990 ? '∞' : hf.toFixed(2)
  const netApyPct = summary.netApyPct != null ? parseFloat(summary.netApyPct) : null

  return (
    <div className="flex items-center gap-5">
      <Chip label="NAV" value={nav} />
      {netApyPct !== null && netApyPct !== undefined && (
        <Chip
          label="Net APY"
          value={netApyPct.toFixed(2) + '%'}
          valueClass={netApyPct >= 0 ? 'text-accent-green' : 'text-accent-red'}
        />
      )}
      <Chip label="Health" value={hfDisplay} valueClass={hfColor(hf)} />
    </div>
  )
}

function Chip({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex flex-col items-center leading-none">
      <span className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</span>
      <span className={`text-sm font-semibold ${valueClass ?? 'text-ink'}`}>{value}</span>
    </div>
  )
}

export type AppTab = 'dashboard' | 'market' | 'earn' | 'trade' | 'games' | 'settings'

const TABS: { id: AppTab; label: string; icon: React.ReactNode; requiresAuth: boolean }[] = [
  {
    id: 'dashboard',
    label: 'Portfolio',
    requiresAuth: true,
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5">
        <rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/>
        <rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/>
      </svg>
    ),
  },
  {
    id: 'market',
    label: 'Market Research',
    requiresAuth: true,
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5">
        <polyline points="1,12 5,7 9,10 15,3"/>
      </svg>
    ),
  },
  {
    id: 'earn',
    label: 'Earn',
    requiresAuth: true,
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
        <circle cx="8" cy="8" r="6.5"/>
        <path d="M5.5 8.5l1.5 1.5 3.5-3.5"/>
        <path d="M8 1.5v1M8 13.5v1M1.5 8h1M13.5 8h1"/>
      </svg>
    ),
  },
  // Trade tab temporarily hidden — iframe embedding of jup.ag is blocked and
  // direct-sign borrow/LP flows trigger Phantom warnings until the domain is
  // allowlisted via review@phantom.com. Reinstate when ready.
  // {
  //   id: 'trade',
  //   label: 'Trade',
  //   requiresAuth: true,
  //   icon: (
  //     <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
  //       <path d="M2 5h10l-2-2M14 11H4l2 2"/>
  //     </svg>
  //   ),
  // },
  {
    id: 'games',
    label: 'Sol Games',
    requiresAuth: true,
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5">
        <rect x="2" y="4" width="12" height="9" rx="1.5"/>
        <circle cx="5.5" cy="8.5" r="1.5"/>
        <circle cx="10.5" cy="8.5" r="1.5"/>
        <line x1="6" y1="3" x2="10" y2="3"/>
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Settings',
    requiresAuth: true,
    icon: (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="w-3.5 h-3.5">
        <circle cx="8" cy="8" r="2.5"/>
        <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41"/>
      </svg>
    ),
  },
]

interface HeaderProps {
  activeTab: AppTab
  onTabChange: (tab: AppTab) => void
  showTabs: boolean
  summary?: PortfolioSummary | null
}

export function Header({ activeTab, onTabChange, showTabs, summary }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const headerRef = useRef<HTMLElement | null>(null)
  const activeTabLabel = TABS.find(t => t.id === activeTab)?.label ?? ''

  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    const onClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClickOutside)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClickOutside)
    }
  }, [menuOpen])

  const handleMobileTab = (id: AppTab) => {
    onTabChange(id)
    setMenuOpen(false)
  }

  return (
    <header ref={headerRef} className="sticky top-0 z-40 flex items-center justify-between border-b border-fringe bg-surface-card px-3 py-2.5 sm:px-6 sm:py-3 gap-2">
        <div className="flex items-center gap-2 sm:gap-5 min-w-0">
          {/* Lab-style brand block — bare logo (no tile) + solquant / quant-core ID */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="/logo.svg" alt="SolQuant" className="h-9 w-9 sm:h-10 sm:w-10 shrink-0" />
            <div className="lab-id whitespace-nowrap truncate">
              solquant <strong>/ quant-core v4.7</strong>
            </div>
            <StreamIndicator />
          </div>

          {showTabs && (
            <div className="hidden sm:flex items-center gap-0.5 sm:gap-1">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={`flex items-center gap-1 sm:gap-1.5 px-2 py-1.5 sm:px-3 rounded-md text-xs font-medium transition-colors
                    ${activeTab === tab.id
                      ? 'bg-accent-purple/10 text-accent-purple ring-1 ring-accent-purple/20'
                      : 'text-ink-muted hover:text-ink hover:bg-surface-hover'}`}
                >
                  {tab.icon}
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {summary && (
          <div className="hidden md:block shrink-0">
            <HeaderMetrics summary={summary} />
          </div>
        )}
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="shrink-0">
            <ConnectWalletButton />
          </div>
          {showTabs && (
            <button
              type="button"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(v => !v)}
              className="sm:hidden p-1.5 rounded-md text-ink-muted hover:text-ink hover:bg-surface-hover transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-5 h-5">
                {menuOpen ? (
                  <>
                    <line x1="5" y1="5" x2="19" y2="19" />
                    <line x1="19" y1="5" x2="5" y2="19" />
                  </>
                ) : (
                  <>
                    <line x1="3" y1="6"  x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </>
                )}
              </svg>
            </button>
          )}
        </div>

      {showTabs && menuOpen && (
        <div
          id="mobile-nav"
          role="menu"
          aria-label={`Navigation — current: ${activeTabLabel}`}
          className="sm:hidden absolute left-0 right-0 top-full border-t border-fringe bg-surface-card shadow-xl max-h-[calc(100vh-3.25rem)] overflow-y-auto"
        >
          {summary && (
            <div className="border-b border-fringe px-4 py-3">
              <HeaderMetrics summary={summary} />
            </div>
          )}
          <nav className="flex flex-col py-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                role="menuitem"
                onClick={() => handleMobileTab(tab.id)}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium text-left transition-colors
                  ${activeTab === tab.id
                    ? 'bg-accent-purple/10 text-accent-purple'
                    : 'text-ink-soft hover:bg-surface-hover hover:text-ink'}`}
              >
                <span className="flex items-center justify-center w-5 h-5 shrink-0">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  )
}
